import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { RoomEnvironment } from 'three/examples/jsm/environments/RoomEnvironment.js';
import { createRolloAnimation, type RolloAnimator } from './RolloAnimation';
import type { SceneGraph } from '../geometry';
import { composeScene, type ModelResolver } from './SceneComposer';

export interface Viewer {
  setModel(graph: SceneGraph, resolveModel?: ModelResolver): void;
  setRollProgress(value: number): void;
  animateRollTo(value: number): void;
  getRollProgress(): number;
  dispose(): void;
}

/** Создать вьюер three.js на canvas: сцена, свет, камера с орбитой, цикл рендера. */
export function createViewer(canvas: HTMLCanvasElement): Viewer {
  const renderer = new THREE.WebGLRenderer({ canvas, antialias: true });
  renderer.outputColorSpace = THREE.SRGBColorSpace;
  renderer.toneMapping = THREE.ACESFilmicToneMapping;
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));

  const scene = new THREE.Scene();
  scene.background = new THREE.Color(0xf3f2ef);

  // IBL: нейтральное студийное окружение (без внешнего HDR) — металл получает
  // корректные отражения, полотно освещается мягко (ADR-0011). PMREM генерится
  // один раз из RoomEnvironment.
  const pmrem = new THREE.PMREMGenerator(renderer);
  const envTexture = pmrem.fromScene(new RoomEnvironment(), 0.04).texture;
  scene.environment = envTexture;

  const camera = new THREE.PerspectiveCamera(45, 1, 0.01, 100);
  camera.position.set(1.5, 1.5, 3);

  const controls = new OrbitControls(camera, canvas);
  controls.enableDamping = true;

  // Мягкий заполняющий и ключевой свет поверх IBL.
  scene.add(new THREE.HemisphereLight(0xffffff, 0x8d8d8d, 0.4));
  const key = new THREE.DirectionalLight(0xffffff, 1.2);
  key.position.set(2, 4, 3);
  scene.add(key);

  let model: THREE.Group | null = null;
  let animator: RolloAnimator | null = null;
  let progress = 0;
  let target = 0;
  let lastTime = 0;
  let needsRender = true;
  controls.addEventListener('change', () => {
    needsRender = true;
  });

  const fitCamera = (obj: THREE.Object3D): void => {
    const box = new THREE.Box3().setFromObject(obj);
    const size = box.getSize(new THREE.Vector3());
    const center = box.getCenter(new THREE.Vector3());
    const maxDim = Math.max(size.x, size.y, size.z) || 1;
    const dist = maxDim * 2.2;
    controls.target.copy(center);
    camera.position.set(center.x + dist * 0.5, center.y + dist * 0.35, center.z + dist);
    camera.near = maxDim / 100;
    camera.far = maxDim * 100;
    camera.updateProjectionMatrix();
    controls.update();
  };

  const resize = (): void => {
    needsRender = true;
    const w = canvas.clientWidth || window.innerWidth;
    const h = canvas.clientHeight || window.innerHeight;
    renderer.setSize(w, h, false);
    camera.aspect = w / h || 1;
    camera.updateProjectionMatrix();
  };
  window.addEventListener('resize', resize);
  resize();

  renderer.setAnimationLoop((time) => {
    const dt = lastTime ? Math.min((time - lastTime) / 1000, 0.25) : 0;
    lastTime = time;
    if (progress !== target) {
      progress += Math.sign(target - progress) * Math.min(Math.abs(target - progress), dt / 5);
      animator?.setProgress(progress);
      needsRender = true;
    }
    controls.update();
    if (needsRender) {
      needsRender = false;
      renderer.render(scene, camera);
    }
  });

  return {
    setModel(graph: SceneGraph, resolveModel?: ModelResolver): void {
      animator?.dispose();
      if (model) scene.remove(model);
      progress = target = 0;
      model = composeScene(graph, resolveModel);
      animator = createRolloAnimation(model, graph);
      needsRender = true;
      scene.add(model);
      fitCamera(model);
    },
    setRollProgress(value: number): void {
      if (!Number.isFinite(value)) return;
      progress = target = Math.min(1, Math.max(0, value));
      animator?.setProgress(progress);
      needsRender = true;
    },
    animateRollTo(value: number): void {
      if (Number.isFinite(value)) target = Math.min(1, Math.max(0, value));
    },
    getRollProgress: () => progress,
    dispose(): void {
      animator?.dispose();
      window.removeEventListener('resize', resize);
      renderer.setAnimationLoop(null);
      controls.dispose();
      envTexture.dispose();
      pmrem.dispose();
      renderer.dispose();
    },
  };
}
